import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { LevelBadge } from "@/components/Badges";

export const nextSeasonName = (s) => {
  const m = /^(\d{4})\D+(\d{4})$/.exec(s || "");
  return m ? `${+m[1] + 1}-${+m[2] + 1}` : "";
};

export default function NextSeasonDialog({ open, onOpenChange, club, onDone }) {
  const [list, setList] = useState([]);
  const [name, setName] = useState("");
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(nextSeasonName(club.season));
    api.get("/sponsors").then(({ data }) => {
      setList(data);
      setSel(Object.fromEntries(data.map((s) => [s.id, { on: true, amount: s.amount }])));
    });
  }, [open, club.season]);

  const upd = (id, patch) => setSel({ ...sel, [id]: { ...sel[id], ...patch } });
  const count = Object.values(sel).filter((x) => x.on).length;

  const submit = async () => {
    if (!window.confirm(`Passer à la saison ${name} en reconduisant ${count} sponsor(s) ? La saison ${club.season} sera archivée.`)) return;
    setBusy(true);
    try {
      const renewals = list.filter((s) => sel[s.id]?.on).map((s) => ({ sponsor_id: s.id, amount: Number(sel[s.id].amount || 0) }));
      const { data } = await api.post("/season/next", { new_season: name, renewals });
      toast.success(`Saison ${data.season} démarrée`);
      onOpenChange(false);
      onDone(data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[640px] max-h-[92vh] overflow-y-auto rounded-[14px]" data-testid="next-season-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Démarrer la saison suivante</DialogTitle>
          <DialogDescription>Cochez les sponsors qui renouvellent. Leurs dates sont décalées d'un an, l'encaissé repart à 0 et les contreparties sont recopiées en « À faire ».</DialogDescription>
        </DialogHeader>
        <label className="block"><span className="field-label">Nouvelle saison</span>
          <input className="field max-w-[200px]" value={name} onChange={(e) => setName(e.target.value)} data-testid="next-season-name-input" />
        </label>
        <ul className="border border-[#E1E4DD] rounded-[12px] divide-y divide-[#E1E4DD]" data-testid="next-season-list">
          {list.map((s) => (
            <li key={s.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
              <label className="flex items-center gap-3 flex-1 min-w-[180px] cursor-pointer">
                <input type="checkbox" className="w-4 h-4 accent-[#1E6B45]" checked={!!sel[s.id]?.on} onChange={(e) => upd(s.id, { on: e.target.checked })} data-testid={`next-season-check-${s.id}`} />
                <span>
                  <span className="font-semibold text-sm">{s.name}</span> <LevelBadge level={s.level} />
                  <span className="block text-xs text-[#5B665F]">Fin actuelle : {fmtDate(s.end_date)}</span>
                </span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <span className="text-[#5B665F]">Montant</span>
                <input type="number" min="0" className="field h-9 w-28" disabled={!sel[s.id]?.on} value={sel[s.id]?.amount ?? ""} onChange={(e) => upd(s.id, { amount: e.target.value })} data-testid={`next-season-amount-${s.id}`} />
                <span>€</span>
              </label>
            </li>
          ))}
          {list.length === 0 && <li className="px-4 py-4 text-sm text-[#5B665F]">Aucun sponsor dans la saison en cours.</li>}
        </ul>
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
          <button className="btn-outline" onClick={() => onOpenChange(false)} data-testid="next-season-cancel">Annuler</button>
          <button className="btn-primary" disabled={busy || !name.trim()} onClick={submit} data-testid="next-season-submit">{busy ? "En cours…" : `Démarrer ${name || "la saison"}`}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
